import { cn } from "@pax/ui";
import { EyeOff } from "lucide-react";
import { useState } from "react";
import { srcSet } from "@/lib/images";
import { type AssetPicture as Picture, placeholderOf } from "../media";

/**
 * An asset's picture, filling whatever box it is given.
 *
 * The box is expected to have the picture's own shape, so `object-cover`
 * crops nothing. Underneath is the picture's thumbhash, which is the same
 * picture a few pixels wide: a grid on a slow connection is already the right
 * mosaic in the right colours before any file arrives. It fades once the
 * picture is drawn over it, since it would otherwise show through wherever the
 * picture is transparent, a blurred copy of its shape around it.
 *
 * An animation moves wherever it is shown, except for someone who has asked
 * for less motion — they get its JPEG still.
 *
 * A sensitive picture is only its thumbhash until someone chooses to look —
 * a handful of colours with nothing in them to recognise, however large it is
 * drawn. In a grid, pointing at it or focusing it is that choice; a touch
 * screen has no pointing, so there it stays hidden until it is opened. Its
 * file isn't fetched before then either, so nothing of it reaches the browser
 * until it is wanted.
 */
export function AssetPicture({
  picture,
  sizes,
  sensitive = false,
  revealed = false,
  hoverReveals = true,
  lookedAt = false,
  priority = false,
  className,
}: {
  picture: Picture;
  /** How wide the picture is drawn, for choosing which file to load. */
  sizes: string;
  sensitive?: boolean;
  /** A sensitive picture someone has already chosen to see. */
  revealed?: boolean;
  /** Whether pointing at a sensitive picture shows it, as it does in a grid. */
  hoverReveals?: boolean;
  /** Whether it has been pointed at or focused yet, which is when a hidden picture's file loads. */
  lookedAt?: boolean;
  priority?: boolean;
  className?: string;
}) {
  const hidden = sensitive && !revealed;
  const loaded = !hidden || (hoverReveals && lookedAt);
  const [drawn, setDrawn] = useState(false);
  return (
    <div className={cn("absolute inset-0 overflow-hidden bg-foreground/5", className)}>
      <div
        aria-hidden
        className={cn(
          "absolute inset-0 bg-cover transition-opacity duration-300",
          drawn && !hidden && "opacity-0",
          drawn && hidden && hoverReveals && "group-hover:opacity-0 group-focus-visible:opacity-0",
        )}
        style={{ backgroundImage: `url(${placeholderOf(picture.thumbhash)})` }}
      />
      {loaded && (
        <picture>
          {picture.animated && (
            <source media="(prefers-reduced-motion: reduce)" srcSet={picture.still} />
          )}
          <img
            src={picture.image.sources.at(-1)?.url}
            srcSet={srcSet(picture.image)}
            sizes={sizes}
            width={picture.width}
            height={picture.height}
            alt=""
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : undefined}
            decoding="async"
            onLoad={() => setDrawn(true)}
            className={cn(
              "relative size-full object-cover transition-opacity duration-300",
              hidden && "opacity-0",
              hidden && hoverReveals && "group-hover:opacity-100 group-focus-visible:opacity-100",
            )}
          />
        </picture>
      )}
      {hidden && hoverReveals && <SensitiveMark />}
    </div>
  );
}

function SensitiveMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300 group-hover:opacity-0 group-focus-visible:opacity-0",
        className,
      )}
    >
      <span className="flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white">
        <EyeOff aria-hidden className="size-3.5" />
        Sensitive
      </span>
    </span>
  );
}
